import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSettings } from "../../App";
import { agentApi } from "./agentApi";
import { OPEN_PLAN_EVENT, PLAN_STARTED_EVENT } from "./constants";
import "./AgentNotifications.css";

/**
 * Agent Studio alerts in the top bar (every screen): a bell with the number of the person's plans that finished
 * since they last looked, a pop-up when one finishes while they are elsewhere ("Plan #38 is ready to review"),
 * and a browser notification when the app is in a background tab (only if they allowed it).
 * Checks every 20 s while one of their plans is building, every 2 minutes otherwise. Hidden when the agent is off.
 */

export const FAST_MS = 20000;
export const SLOW_MS = 120000;

const label = (n) =>
  n.status === "awaiting_approval"
    ? "Ready to review"
    : n.status === "blocked"
    ? `Ready — ${n.fails || "some"} check${n.fails === 1 ? "" : "s"} to fix`
    : "Failed";

/** Ask once (from a click) whether the browser may show plan alerts when the app is in another tab. */
export const askDesktopAlerts = () => {
  try {
    if (typeof window === "undefined" || !("Notification" in window) || window.Notification.permission !== "default") return;
    if (window.localStorage.getItem("agent-studio-alerts-asked")) return;
    window.localStorage.setItem("agent-studio-alerts-asked", "1");
    window.Notification.requestPermission();
  } catch (e) {
    // not supported — the bell still works
  }
};

const desktopAlert = (n, open) => {
  try {
    if (!("Notification" in window) || window.Notification.permission !== "granted" || !document.hidden) return;
    const a = new window.Notification(`Plan #${n.id}: ${label(n)}`, { body: n.title, tag: `agent-plan-${n.id}` });
    a.onclick = () => {
      window.focus();
      open(n.id);
      a.close();
    };
  } catch (e) {
    // ignore
  }
};

const AgentNotifications = () => {
  const settings = useSettings();
  const baseUrl = settings?.BACKEND_URL;
  const api = useMemo(() => agentApi(baseUrl), [baseUrl]);
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null); // { enabled, unread, building, items }
  const [open, setOpen] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [perm, setPerm] = useState(() => (typeof window !== "undefined" && "Notification" in window ? window.Notification.permission : "unsupported"));
  const known = useRef(null); // unread ids already seen by this tab (null = first check: no pop-ups for old ones)
  const timer = useRef(null);
  const onStudio = location.pathname.startsWith("/agent_studio");
  const onStudioRef = useRef(onStudio);
  onStudioRef.current = onStudio;
  const box = useRef(null);

  const openPlan = useCallback(
    (id) => {
      setOpen(false);
      setToasts((t) => t.filter((x) => x.id !== id));
      setData((d) => (d ? { ...d, unread: Math.max(0, d.unread - (d.items.find((i) => i.id === id && i.unread) ? 1 : 0)), items: d.items.map((i) => (i.id === id ? { ...i, unread: false } : i)) } : d));
      navigate(`/agent_studio?run=${id}`);
      // Agent Studio already open: it shows the plan without reloading.
      setTimeout(() => window.dispatchEvent(new CustomEvent(OPEN_PLAN_EVENT, { detail: { id } })), 0);
    },
    [navigate]
  );

  const check = useCallback(async () => {
    clearTimeout(timer.current);
    let next = SLOW_MS;
    try {
      const d = await api.notifications();
      if (!d || d.enabled === false) {
        setData(null);
        return; // agent switched off: no bell, no more checks
      }
      setData(d);
      const unread = (d.items || []).filter((i) => i.unread);
      if (known.current) {
        const fresh = unread.filter((i) => !known.current.has(i.id));
        if (fresh.length) {
          // On Agent Studio itself the plan is on screen — the bell count is enough.
          if (!onStudioRef.current) setToasts((t) => [...fresh.map((i) => ({ ...i, at: Date.now() })), ...t.filter((x) => !fresh.some((f) => f.id === x.id))].slice(0, 3));
          fresh.forEach((i) => desktopAlert(i, openPlan));
        }
      }
      known.current = new Set(unread.map((i) => i.id));
      next = d.building ? FAST_MS : SLOW_MS;
    } catch (e) {
      if (e?.status === 401 || e?.status === 403 || e?.status === 404) {
        setData(null);
        return;
      }
    }
    timer.current = setTimeout(check, next);
  }, [api, openPlan]);

  useEffect(() => {
    if (!baseUrl) return undefined;
    check();
    const started = () => setTimeout(check, 1500); // a plan was just started: check soon and then every 20 s
    window.addEventListener(PLAN_STARTED_EVENT, started);
    return () => {
      clearTimeout(timer.current);
      window.removeEventListener(PLAN_STARTED_EVENT, started);
    };
  }, [baseUrl, check]);

  // Pop-ups close themselves after 15 s.
  useEffect(() => {
    if (!toasts.length) return undefined;
    const t = setTimeout(() => setToasts((all) => all.filter((x) => Date.now() - x.at < 15000)), 15500);
    return () => clearTimeout(t);
  }, [toasts]);

  // Close the list on an outside click.
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (box.current && !box.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (!data) return null;
  const unread = data.unread || 0;

  return (
    <div className="nm-bell" ref={box}>
      <button
        type="button"
        className="nm-bell-btn"
        onClick={() => {
          setOpen((v) => !v);
          setToasts([]); // the list shows the same plans; pop-ups would cover it
        }}
        title="Agent Studio plans"
        aria-label={unread ? `Agent Studio: ${unread} new` : "Agent Studio plans"}
        aria-expanded={open}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && <span className="nm-bell-badge">{unread > 9 ? "9+" : unread}</span>}
        {data.building && <span className="nm-bell-dot" title="A plan is building" />}
      </button>

      {open && (
        <div className="nm-bell-list" role="dialog" aria-label="Agent Studio plans">
          <div className="nm-bell-head">
            <strong>Agent Studio</strong>
            {unread > 0 && (
              <button
                type="button"
                className="nm-bell-link"
                onClick={async () => {
                  setData((d) => ({ ...d, unread: 0, items: d.items.map((i) => ({ ...i, unread: false })) }));
                  try {
                    await api.notificationsSeen();
                  } catch (e) {
                    // next check shows the real state
                  }
                }}
              >
                Mark all as read
              </button>
            )}
          </div>
          {data.building && <div className="nm-bell-building">A plan is building — you will be told here when it is ready.</div>}
          {data.items.length === 0 ? (
            <div className="nm-bell-empty">No finished plans in the last 7 days.</div>
          ) : (
            <ul>
              {data.items.map((n) => (
                <li key={n.id}>
                  <button type="button" className={`nm-bell-item${n.unread ? " nm-bell-item--new" : ""}`} onClick={() => openPlan(n.id)}>
                    <span className={`nm-bell-status nm-bell-status--${n.status}`}>
                      Plan #{n.id} · {label(n)}
                    </span>
                    <span className="nm-bell-title">{n.title}</span>
                    {n.error && <span className="nm-bell-error">{n.error}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {perm === "default" && (
            <button
              type="button"
              className="nm-bell-link nm-bell-desktop"
              onClick={async () => {
                try {
                  setPerm(await window.Notification.requestPermission());
                } catch (e) {
                  setPerm("unsupported");
                }
              }}
            >
              Also alert me when the app is in another tab
            </button>
          )}
        </div>
      )}

      {toasts.length > 0 && (
        <div className="nm-bell-toasts" aria-live="polite">
          {toasts.map((n) => (
            <div key={n.id} className={`nm-bell-toast nm-bell-toast--${n.status}`} role="status">
              <div className="nm-bell-toast-body">
                <strong>
                  Plan #{n.id} · {label(n)}
                </strong>
                <span>{n.title}</span>
              </div>
              <button type="button" className="nm-bell-open" onClick={() => openPlan(n.id)}>
                Open
              </button>
              <button type="button" className="nm-bell-x" aria-label="Close" onClick={() => setToasts((t) => t.filter((x) => x.id !== n.id))}>
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentNotifications;
